import { useContext, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import { Grid, InputAdornment } from '@mui/material'
import { format } from 'date-fns'

import { TextFieldComponent } from 'src/components/TextFieldComponent'
import { ResponsibleCard } from 'src/components/ResponsibleCard'
import { ModalFullScreen } from 'src/components/ModalFullScreen'
import { TextFieldMask } from 'src/components/TextFieldMask'
import { composePath, navigateToInstitute } from 'src/utils'
import { CheckboxComponent } from 'src/components/Checkbox'
import { EditButton } from 'src/components/EditButton'
import { FileDetail } from 'src/components/FileDetail'
import { BoxButtons } from 'src/components/BoxButton'
import DataContext from 'src/contexts/DataContext'
import { Stepper } from 'src/components/Stepper'
import { Button } from 'src/components/Button'
import * as S from './styles'
import { ptBR } from 'date-fns/locale'
import { useCourseInfoHook } from 'src/hooks/useCourseInfoHook'

const ConfirmationPage = () => {
  const navigate = useNavigate()

  const [courseLogo, setCourseLogo] = useState<string>('')
  const [currentResponsibleIndex, setCurrentResponsibleIndex] =
    useState<number>(0)
  const [isResponsableModalOpen, setIsResponsableModalOpen] =
    useState<boolean>(false)

  const { registration } = useContext(DataContext)

  const currentCourseInfo = useCourseInfoHook({ course: registration.course! })

  const currentStep = registration.getStep(registration, 'confirmation')

  const documentsFormData = registration.getStep(
    registration,
    'documents'
  )?.formData
  const candidateDataFormData = registration.getStep(
    registration,
    'candidateData'
  )?.formData
  const addressFormData = registration.getStep(
    registration,
    'address'
  )?.formData
  const searchFormData = registration.getStep(registration, 'search')?.formData
  const responsiblesFormData = registration.getStep(
    registration,
    'responsibles'
  )?.formData

  const currentResponsible = responsiblesFormData?.responsibleList?.length
    ? responsiblesFormData?.responsibleList[currentResponsibleIndex]
    : undefined

  const handleViewResponsible = (index: number) => {
    setCurrentResponsibleIndex(index)
    setIsResponsableModalOpen(true)
  }

  const formatDate = (date: string) =>
    format(new Date(`${date} 00:00:00`), 'P', { locale: ptBR })

  const downloadFileFromMemory = (file: File) => {
    if (file) {
      const a = document.createElement('a')
      a.href = window.URL.createObjectURL(file)
      a.download = file.name
      a.click()
    }
  }

  const formatFileSize = (size: number) => `${Math.round(size / 1024)} Kb`

  useEffect(() => {
    if (!registration.course)
      navigate(`/admissao/${localStorage.getItem('course')}`)
  }, [])

  useEffect(() => {
    currentCourseInfo && setCourseLogo(currentCourseInfo.logo_url)
  }, [currentCourseInfo])

  return (
    <S.ContainerFlex>
      <S.Content>
        <Stepper
          currentStepName={currentStep?.name}
          label={currentStep?.title}
          course={registration.course}
        />
        <S.HeaderContainer>
          <S.Title>Confira com atenção todos os dados preenchidos</S.Title>
          <S.BlueTitle>
            Caso tenha alguma informação incorreta, clique no botão "editar"
            para alterar.
          </S.BlueTitle>
        </S.HeaderContainer>

        <S.HeaderWithImageContainer>
          <S.LogoImage src={courseLogo} alt="Logo Germinare" />
          <S.HeaderContainer>
            <S.Subtitle>
              Inscrição para a escola Germinare{' '}
              <S.CourseName>{`${registration.course}`}</S.CourseName>
            </S.Subtitle>
          </S.HeaderContainer>
        </S.HeaderWithImageContainer>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <S.SectionHeader>
              <S.SectionTitle>Documentos obrigatórios</S.SectionTitle>
              <EditButton
                onClick={() =>
                  navigate(
                    composePath(
                      '/documentos',
                      `admissao/${registration.course}`
                    )
                  )
                }
              ></EditButton>
            </S.SectionHeader>
          </Grid>
          <Grid item xs={12}>
            <S.SectionSubtitle>
              CPF DO(A) <span>CANDIDATO(A)</span>
            </S.SectionSubtitle>
          </Grid>
          <Grid item xs={12}>
            <S.HalfWidthContainer className="full-row-mobile">
              <TextFieldComponent
                label={'CPF do(a) candidato(a)'}
                id="cpf"
                value={registration.cpf}
                InputProps={{ readOnly: true, disabled: true }}
              />
            </S.HalfWidthContainer>
          </Grid>
          <Grid item xs={12}>
            <S.SectionSubtitle>COMPROVANTE DE ESCOLARIDADE</S.SectionSubtitle>
          </Grid>
          <Grid item xs={12}>
            {documentsFormData?.uploadFile ? (
              <S.HalfWidthContainer className="full-row-mobile">
                <FileDetail
                  name={documentsFormData?.uploadFile.name}
                  size={formatFileSize(documentsFormData?.uploadFile.size)}
                  onClickDownload={() =>
                    downloadFileFromMemory(documentsFormData?.uploadFile)
                  }
                ></FileDetail>
              </S.HalfWidthContainer>
            ) : (
              <S.MissingFile>
                Declaração escolar não enviada. Favor enviar posteriormente na
                Área do Candidato.
              </S.MissingFile>
            )}
          </Grid>
          <Grid item xs={12}>
            <S.SectionHeader>
              <S.SectionTitle>Dados do(a) candidato(a)</S.SectionTitle>
              <EditButton
                onClick={() =>
                  navigate(
                    composePath(
                      '/dados-pessoais',
                      `admissao/${registration.course}`
                    )
                  )
                }
              ></EditButton>
            </S.SectionHeader>
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent
              label={'Nome'}
              id="name"
              fullWidth
              value={candidateDataFormData?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Sexo'}
              id="sex"
              fullWidth
              value={candidateDataFormData?.sex.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Data de nascimento'}
              id="birth-date"
              fullWidth
              value={formatDate(candidateDataFormData.birth_date)}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldMask
              mask="(99) 99999-9999"
              label="WhatsApp do(a) candidato(a)"
              value={candidateDataFormData?.whatsapp}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <S.WhatsApp />
                  </InputAdornment>
                ),
                readOnly: true,
                disabled: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              fullWidth
              id="email"
              label="E-mail do(a) candidato(a)"
              value={candidateDataFormData?.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <S.Email />
                  </InputAdornment>
                ),
                readOnly: true,
                disabled: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextFieldComponent
              label={'RG do(a) candidato(a)'}
              id="rg"
              fullWidth
              value={candidateDataFormData?.document}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextFieldComponent
              label={'Órgão expedidor'}
              id="issuer"
              fullWidth
              value={candidateDataFormData?.issue_entity?.acronym}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextFieldComponent
              label={'Estado emissor'}
              id="issuer-state"
              fullWidth
              value={candidateDataFormData?.issue_state?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextFieldComponent
              label="Data de emissão"
              InputProps={{ readOnly: true, disabled: true }}
              value={formatDate(candidateDataFormData.issue_date)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              fullWidth
              id="motherName"
              label="Nome completo da mãe"
              value={candidateDataFormData?.motherFullName}
              InputProps={{
                readOnly: true,
                disabled: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              fullWidth
              id="fatherName"
              label="Nome completo do pai"
              value={candidateDataFormData?.fatherFullName}
              InputProps={{
                readOnly: true,
                disabled: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <S.SectionHeader>
              <S.SectionTitle>Endereço do(a) candidato(a)</S.SectionTitle>
              <EditButton
                onClick={() =>
                  navigate(
                    composePath('/endereco', `admissao/${registration.course}`)
                  )
                }
              />
            </S.SectionHeader>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'CEP do candidato(a)'}
              id="address-postal_code"
              fullWidth
              value={addressFormData?.postal_code}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'Nome da rua'}
              id="address-street"
              fullWidth
              value={addressFormData?.street}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'Número'}
              id="address-number"
              fullWidth
              value={addressFormData?.number}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'Bairro'}
              id="address-neighborhood"
              fullWidth
              value={addressFormData?.neighborhood}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'Cidade'}
              id="address-city"
              fullWidth
              value={addressFormData?.city}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextFieldComponent
              label={'Estado (UF)'}
              id="address-state"
              fullWidth
              value={addressFormData?.state}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldComponent
              label={'Complemento'}
              id="address-extra"
              fullWidth
              value={addressFormData?.complemento}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.SectionHeader>
              <S.SectionTitle>Sobre a escola</S.SectionTitle>
              <EditButton
                onClick={() =>
                  navigate(
                    composePath('/pesquisa', `admissao/${registration.course}`)
                  )
                }
              ></EditButton>
            </S.SectionHeader>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Estado'}
              id="search-state"
              fullWidth
              value={searchFormData?.state?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Cidade'}
              id="search-city"
              fullWidth
              value={searchFormData?.city?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Escola'}
              id="search-school"
              fullWidth
              value={searchFormData?.school?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Período em que estuda'}
              id="search-studing_period_type"
              fullWidth
              value={searchFormData?.studing_period_type?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Tipo de instituição'}
              id="search-instituition-type"
              fullWidth
              value={searchFormData?.institution_type?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              label={'Como conheceu a Germinare?'}
              id="search-school_met"
              fullWidth
              value={searchFormData?.school_met?.name}
              InputProps={{ readOnly: true, disabled: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <S.SectionHeader>
              <S.SectionTitle>Dados do(a) responsável</S.SectionTitle>
            </S.SectionHeader>
          </Grid>
        </Grid>

        {responsiblesFormData?.responsibleList?.length &&
          responsiblesFormData?.responsibleList.map(
            (responsible: any, index: number) => (
              <ResponsibleCard
                key={index}
                title={`Responsável ${index + 1}`}
                name={responsible.name}
                relationName={responsible.relationship.name}
                handleEditResponsible={() =>
                  navigate(
                    composePath(
                      '/responsaveis',
                      `admissao/${registration.course}`
                    ),
                    {
                      state: { responsibleIndex: index },
                    }
                  )
                }
                handleViewResponsible={() => handleViewResponsible(index)}
              />
            )
          )}

        <BoxButtons
          onClickPrevius={() =>
            navigate(
              composePath('/responsaveis', `admissao/${registration.course}`)
            )
          }
          onClickNext={() =>
            navigate(composePath('/termos', `admissao/${registration.course}`))
          }
          isLoading={false}
        />

        <ModalFullScreen
          isOpen={isResponsableModalOpen}
          onCloseModal={() => setIsResponsableModalOpen(false)}
        >
          <S.ModalBox>
            <S.Header>
              <S.ModalTitle>Dados do Responsável</S.ModalTitle>
            </S.Header>

            <S.ModalDescription>{`Responsável ${
              currentResponsibleIndex + 1
            }`}</S.ModalDescription>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6} sm={6}>
                <TextFieldComponent
                  label={'Nome completo do(a) responsável'}
                  fullWidth
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.name}
                />
              </Grid>
              <Grid item xs={12} md={6} sm={6}>
                <TextFieldComponent
                  label={'Estado civil'}
                  fullWidth
                  required
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.civil_state?.name}
                />
              </Grid>
              <Grid item xs={12} md={6} sm={6}>
                <TextFieldComponent
                  label="Sexo"
                  fullWidth
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.sex?.name}
                />
              </Grid>
              <Grid item xs={12} md={6} sm={6}>
                <TextFieldComponent
                  label="Parentesco"
                  fullWidth
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.relationship?.name}
                />
              </Grid>
              <Grid item xs={12} md={6} sm={6}>
                <TextFieldComponent
                  label="Data de nascimento"
                  fullWidth
                  InputProps={{ readOnly: true, disabled: true }}
                  value={formatDate(currentResponsible.birth_date)}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextFieldMask
                  mask="999.999.999-99"
                  label="CPF do(a) responsável"
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.cpf}
                />
              </Grid>

              <Grid item sm={6} xs={12}>
                <S.FormControl
                  control={
                    <CheckboxComponent
                      checked={currentResponsible?.is_foreign}
                    />
                  }
                  label={
                    <S.Typograph>RESPONSÁVEL É ESTRANGEIRO(A)</S.Typograph>
                  }
                  disabled
                />
              </Grid>

              <Grid item sm={6} xs={12}>
                <TextFieldComponent
                  label="Nacionalidade"
                  required
                  fullWidth
                  InputProps={{ readOnly: true, disabled: true }}
                  value={currentResponsible?.nationality?.name}
                />
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item lg={3} xs={12}>
                  <TextFieldComponent
                    fullWidth
                    InputProps={{ readOnly: true, disabled: true }}
                    label={`${
                      currentResponsible?.is_foreign ? 'RNE' : 'RG'
                    } do(a) responsável`}
                    value={currentResponsible?.document}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <TextFieldComponent
                    label="Órgão expedidor"
                    InputProps={{ readOnly: true, disabled: true }}
                    value={currentResponsible?.issue_entity?.acronym}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <TextFieldComponent
                    label="Estado emissor"
                    InputProps={{ readOnly: true, disabled: true }}
                    value={currentResponsible?.issue_state?.name}
                  />
                </Grid>
                <Grid item lg={3} xs={12}>
                  <TextFieldComponent
                    label="Data de emissão"
                    InputProps={{ readOnly: true, disabled: true }}
                    value={formatDate(currentResponsible.issue_date)}
                  />
                </Grid>
              </Grid>

              <Grid item sm={6} xs={12}>
                <TextFieldMask
                  mask="(99) 99999-9999"
                  label="WhatsApp do(a) responsável"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.WhatsApp />
                      </InputAdornment>
                    ),
                    readOnly: true,
                    disabled: true,
                  }}
                  value={currentResponsible?.whatsapp}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextFieldComponent
                  fullWidth
                  label="E-mail do(a) responsável"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <S.Email />
                      </InputAdornment>
                    ),
                    readOnly: true,
                    disabled: true,
                  }}
                  value={currentResponsible?.email}
                />
              </Grid>

              <Grid item sm={12} xs={12}>
                <S.FormControl
                  control={
                    <CheckboxComponent
                      checked={currentResponsible?.hasAnotherAddress}
                    />
                  }
                  label={
                    <S.Typograph>ENDEREÇO DIFERENTE DO CANDIDATO</S.Typograph>
                  }
                  disabled
                />
              </Grid>

              {!!currentResponsible?.hasAnotherAddress && (
                <>
                  <Grid item xs={12} md={4}>
                    <TextFieldMask
                      mask="99999-999"
                      label="CEP do(a) responsável"
                      variant="outlined"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.postal_code}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextFieldComponent
                      label="Nome da rua"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.street}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextFieldComponent
                      type="number"
                      label="Número"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.number}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextFieldComponent
                      label="Bairro"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.neighborhood}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextFieldComponent
                      label="Cidade"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.city}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextFieldComponent
                      label="Estado (UF)"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.state}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldComponent
                      label="Complemento"
                      InputProps={{ readOnly: true, disabled: true }}
                      value={currentResponsible?.complemento}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <S.ModalBoxButtons>
              <Button
                variant={'contained'}
                onClick={() => setIsResponsableModalOpen(false)}
              >
                Fechar
              </Button>
            </S.ModalBoxButtons>
          </S.ModalBox>
        </ModalFullScreen>
      </S.Content>
    </S.ContainerFlex>
  )
}

export { ConfirmationPage }
